import {
  DateField,
  getDefaultSortOrder,
  ListButton,
  Show,
  TagField,
  TextField,
} from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";

import { Divider, Space, Table, Tag, Typography } from "antd";
import { IResourceComponentsProps, useShow, useCustom, useApiUrl } from "@refinedev/core";
import { IBulkEmailJob, IVesselEmailTask } from "interfaces";
import React, { useEffect } from "react";
import { getStatusTagColor } from "constants/tagColors";
import { ShowSection, ShowField, ShowFieldGrid } from "components/show";
import { MailOutlined, UnorderedListOutlined } from "@ant-design/icons";

const { Text } = Typography;

export const BulkEmailJobsShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IBulkEmailJob>();
  const record = result;
  const apiUrl = useApiUrl();

  const {
    query: {
      isLoading: isVesselTasksLoading,
      refetch: refetchVesselTasks
    },

    result: vesselTasksData
  } = useCustom<IVesselEmailTask[]>({
    url: `${apiUrl}/entm-vessel-email-status`,
    method: "get",
    queryOptions: {
      enabled: !!record?.id,
    },
    config: {
      query: {
        join: "bulkEmailJob",
        filter: `bulkEmailJob.id||$eq||${record?.id}`,
      },
    },
  });

  const vesselTasks = Array.isArray(vesselTasksData?.data)
    ? vesselTasksData?.data
    : (vesselTasksData?.data as any)?.data || [];

  const tableProps = {
    dataSource: vesselTasks,
    pagination: {
      pageSize: 10,
      showSizeChanger: true,
      showQuickJumper: true,
      total: vesselTasks.length,
    },
  };

  const sorter: any[] = [];

  useEffect(() => {
    if (record?.id) {
      refetchVesselTasks();
    }
  }, [record?.id, refetchVesselTasks]);

  return (
    <Show
      breadcrumb={false}
      isLoading={isLoading}
      headerProps={{
        title: "eNTM Email Job Details",
        onBack: () => window.history.back(),
        subTitle: "job details and vessel email status",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        extra: (
          <React.Fragment>
            <Space>
              <ListButton resource="bulk-email">eNTM Email Jobs</ListButton>
            </Space>
          </React.Fragment>
        ),
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <ShowSection
          title="Job Information"
          subtitle="Email job details and delivery status"
          icon={<MailOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Job Name" value={record?.name} />
            <ShowField label="Description" value={record?.description} type="notes" />
            <ShowField
              label="Status"
              value={record?.status}
              type="status"
              tagColor={getStatusTagColor(record?.status || "")}
            />
            <ShowField label="Created By" value={record?.createdBy} />
            <ShowField
              label="Successful Emails"
              value={record?.successfulEmails || 0}
              type="tag"
              tagColor="green"
            />
            <ShowField
              label="Failed Emails"
              value={record?.failedEmails || 0}
              type="tag"
              tagColor={record?.failedEmails && record.failedEmails > 0 ? "red" : "green"}
            />
          </ShowFieldGrid>
          {record?.errorMessage && (
            <ShowFieldGrid columns={1}>
              <ShowField label="Error Message" value={record.errorMessage} type="notes" />
            </ShowFieldGrid>
          )}
        </ShowSection>

        <ShowSection
          title="Vessel Email Tasks"
          subtitle={`${vesselTasks.length} vessel(s) in this job`}
          icon={<UnorderedListOutlined />}
          variant="elevated"
        >
          <Table
            {...tableProps}
            rowKey="id"
            size="small"
            loading={isVesselTasksLoading}
          >
            <Table.Column
              dataIndex="vesselName"
              key="vesselName"
              title="Vessel Name"
              render={(value) => <TextField value={value} />}
              defaultSortOrder={getDefaultSortOrder("vesselName", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="vesselImoNumber"
              key="vesselImoNumber"
              title="IMO Number"
              render={(value) => <TextField value={value} />}
              defaultSortOrder={getDefaultSortOrder("vesselImoNumber", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="vesselEmail"
              key="vesselEmail"
              title="Email"
              render={(value) => <CopyableEmailTag value={value} />}
              defaultSortOrder={getDefaultSortOrder("vesselEmail", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="status"
              key="status"
              title="Status"
              render={(value) => (
                <TagField color={getStatusTagColor(value)} value={value} />
              )}
              defaultSortOrder={getDefaultSortOrder("status", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="sentAt"
              key="sentAt"
              title="Sent At"
              render={(value) =>
                value ? <DateField value={value} format="DD-MMM-YYYY HH:mm" /> : <Text>N/A</Text>
              }
              defaultSortOrder={getDefaultSortOrder("sentAt", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="retryCount"
              key="retryCount"
              title="Retry Count"
              render={(value, record: IVesselEmailTask) => (
                <Text>{value || 0}/{record.maxRetries || 3}</Text>
              )}
              defaultSortOrder={getDefaultSortOrder("retryCount", sorter)}
              sorter
            />
            <Table.Column
              dataIndex="errorMessage"
              key="errorMessage"
              title="Error Message"
              render={(value) => (
                <TextField value={value || "N/A"} ellipsis />
              )}
              ellipsis
            />
          </Table>
        </ShowSection>
      </Space>
    </Show>
  );
};
